import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Star, Loader2, Save, Search } from "lucide-react";
import { api, apiErr } from "../../lib/api";
import { rupee } from "../../lib/format";
import { Panel, EntitySelect } from "./ui";
import { Header } from "./CorporateDashboard";
import { VegBadge } from "../../components/customer/ItemCard";

function scopedRestaurants(ctx, user) {
  let list = ctx.restaurants;
  if (["restaurant_admin", "restaurant_operator"].includes(user.role)) {
    list = list.filter((r) => (user.restaurant_ids || []).includes(r.id));
  } else if (user.role === "restaurant_group_admin") {
    list = list.filter((r) => r.group_id === user.group_id);
  }
  return list;
}

const MEALS = [
  { value: "all", label: "All day" }, { value: "breakfast", label: "Breakfast" },
  { value: "lunch", label: "Lunch" }, { value: "dinner", label: "Dinner" },
];

export default function MenuManager({ ctx, user }) {
  const rests = scopedRestaurants(ctx, user);
  const [rid, setRid] = useState(rests[0]?.id);
  const [items, setItems] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(null);
  const [cat, setCat] = useState("All");
  const [veg, setVeg] = useState("all");
  const [q, setQ] = useState("");

  const load = () => rid && api.get(`/menu/${rid}`).then((r) => {
    setItems(r.data);
    setEdits(Object.fromEntries(r.data.map((x) => [x.id, {
      price: x.price, meal_period: x.meal_period || "all",
      stock_qty: x.stock_qty ?? "",
    }])));
  });
  useEffect(() => { setItems(null); load(); }, [rid]);

  const categories = useMemo(() => ["All", ...Array.from(new Set((items || []).map((i) => i.category)))], [items]);
  const filtered = useMemo(() => (items || []).filter((i) =>
    (cat === "All" || i.category === cat) &&
    (veg === "all" || i.food_type === veg) &&
    (!q || i.name.toLowerCase().includes(q.toLowerCase()))
  ), [items, cat, veg, q]);

  const setField = (id, k, v) => setEdits((p) => ({ ...p, [id]: { ...p[id], [k]: v } }));

  const toggle = async (item) => {
    try {
      const { data } = await api.patch(`/menu/item/${item.id}`, { available: !item.available });
      setItems((p) => p.map((x) => (x.id === item.id ? data : x)));
      toast.success(`${item.name} · ${data.available ? "Available" : "Sold Out"}`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const saveRow = async (item) => {
    setSaving(item.id);
    try {
      const e = edits[item.id];
      const { data } = await api.patch(`/menu/item/${item.id}`, {
        price: e.price, meal_period: e.meal_period, stock_qty: e.stock_qty === "" ? null : e.stock_qty,
      });
      setItems((p) => p.map((x) => (x.id === item.id ? data : x)));
      toast.success(`${item.name} updated`);
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(null); }
  };

  const selector = (
    <EntitySelect label="Restaurant" value={rid} onChange={setRid} testid="menu-restaurant-select"
      options={rests.map((r) => ({ value: r.id, label: r.name }))} />
  );

  if (!rid) return <Header title="Menu Manager" sub="No restaurant assigned" />;

  return (
    <div className="space-y-5">
      <Header title="Menu Manager" sub="Edit price, timing, stock & availability — reflects instantly on the customer menu" right={selector} />

      <Panel>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <EntitySelect label="Category" value={cat} onChange={setCat} testid="menu-cat-filter"
            options={categories.map((c) => ({ value: c, label: c }))} />
          <div className="flex items-center gap-1 card-surface-2 rounded-lg p-1" data-testid="veg-filter">
            {[["all", "All"], ["veg", "Veg"], ["non-veg", "Non-veg"]].map(([v, l]) => (
              <button key={v} onClick={() => setVeg(v)}
                className={`px-3 py-1 rounded-md text-xs font-semibold ${veg === v ? "gold-gradient text-slate-950" : "text-slate-300"}`}
                data-testid={`veg-${v}`}>{l}</button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[160px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search items"
              className="w-full bg-[#16336B] border hairline rounded-lg pl-8 pr-3 py-1.5 text-sm focus:border-amber-500 outline-none" data-testid="menu-search" />
          </div>
          <button disabled className="card-surface-2 rounded-lg px-3 py-1.5 text-xs text-slate-500 cursor-not-allowed" title="CSV upload coming soon" data-testid="menu-upload">
            Upload / Replace (CSV) — soon
          </button>
        </div>

        {!items ? <Loader2 className="animate-spin text-amber-400" /> : (
          <div className="space-y-2" data-testid="menu-items-list">
            {filtered.map((it) => {
              const e = edits[it.id] || {};
              return (
                <div key={it.id} className="card-surface-2 rounded-xl p-3 flex flex-wrap items-center gap-3" data-testid={`menu-item-${it.id}`}>
                  <img src={it.image} alt={it.name} className="h-12 w-12 rounded-lg object-cover shrink-0" />
                  <div className="min-w-[150px] flex-1">
                    <div className="flex items-center gap-2"><VegBadge type={it.food_type} /><span className="font-semibold text-sm">{it.name}</span></div>
                    <div className="text-[11px] text-slate-400">{it.category} · <Star size={9} className="inline text-amber-400 fill-amber-400" /> {it.rating}</div>
                  </div>
                  <label className="text-[11px] text-slate-400">₹<input type="number" value={e.price ?? ""} onChange={(ev) => setField(it.id, "price", ev.target.value)}
                    className="w-20 ml-1 bg-[#08183A] border hairline rounded-md px-2 py-1 text-sm text-slate-100 focus:border-amber-500 outline-none" data-testid={`price-${it.id}`} /></label>
                  <select value={e.meal_period || "all"} onChange={(ev) => setField(it.id, "meal_period", ev.target.value)}
                    className="bg-[#08183A] border hairline rounded-md px-2 py-1 text-xs focus:border-amber-500 outline-none" data-testid={`meal-${it.id}`}>
                    {MEALS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                  <label className="text-[11px] text-slate-400">Qty<input type="number" value={e.stock_qty ?? ""} placeholder="∞" onChange={(ev) => setField(it.id, "stock_qty", ev.target.value)}
                    className="w-16 ml-1 bg-[#08183A] border hairline rounded-md px-2 py-1 text-sm text-slate-100 focus:border-amber-500 outline-none" data-testid={`qty-${it.id}`} /></label>
                  <button onClick={() => saveRow(it)} disabled={saving === it.id} data-testid={`save-item-${it.id}`}
                    className="card-surface rounded-lg px-3 py-1.5 text-xs font-semibold hover:border-amber-500 flex items-center gap-1.5">
                    {saving === it.id ? <Loader2 className="animate-spin" size={13} /> : <Save size={13} className="text-amber-400" />} Save
                  </button>
                  <button onClick={() => toggle(it)}
                    className={`shrink-0 text-xs font-bold px-3 py-1.5 rounded-full ${it.available ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}
                    data-testid={`toggle-availability-${it.id}`}>
                    {it.available ? "AVAILABLE" : "SOLD OUT"}
                  </button>
                </div>
              );
            })}
            {filtered.length === 0 && <p className="text-slate-600 text-sm py-8 text-center">No items match these filters.</p>}
          </div>
        )}
      </Panel>
    </div>
  );
}
